import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input, Button, Select, Table, InputNumber, Typography, Empty, Spin,
  Modal, Radio, message, Tag, Checkbox,
} from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { ClienteFormModal } from '../../shared/components/ClienteFormModal';

const { Title, Text } = Typography;

const fmt = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;

export const VendedorPedido = () => {
  const { theme, clientPath } = useClient();
  const navigate = useNavigate();
  const primary = theme?.primaryColor || '#111827';

  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);

  // Las líneas viven en la pantalla hasta confirmar: el pedido se crea de una,
  // con todo junto. Si se cierra sin confirmar, no queda nada a medias.
  const [lineas, setLineas] = useState([]);
  const [estadoPago, setEstadoPago] = useState('pendiente');
  const [metodos, setMetodos] = useState([]);
  const [metodo, setMetodo] = useState(null);
  const [avisar, setAvisar] = useState(false);
  const [obs, setObs] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [modal, setModal] = useState(false);
  const [altaCliente, setAltaCliente] = useState(false);
  // Las promos las calcula el server con el mismo evaluador del carrito, para
  // que el vendedor vea el mismo precio que ve el cliente.
  const [promos, setPromos] = useState({ promos: [], descuento: 0, envio_gratis: false });

  useEffect(() => {
    (async () => {
      try {
        const [resCli, resProd, resMet] = await Promise.all([
          api.admin.clientes(),
          api.admin.productos(),
          api.pagos.getMetodos(),
        ]);
        setClientes(resCli.data || []);
        setProductos(resProd.data?.items || []);
        setMetodos(resMet.data || []);
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const buscarProductos = useCallback(async (texto) => {
    const res = await api.admin.productos(texto);
    setProductos(res.data?.items || []);
  }, []);

  // La lista del cliente decide el precio. Sin cliente elegido se muestra
  // Minorista de referencia; el precio final igual lo resuelve el servidor.
  const listaDelCliente = clientes.find(c => c.id === cliente)?.idpreciolista || 1;

  const precioDe = (precios) => {
    if (!precios?.length) return null;
    const deLaLista = precios.find(p => p.id_tipo_precio === listaDelCliente);
    return (deLaLista || precios[0]).precio;
  };

  // Cada variante es una fila propia: es lo que se vende y lo que tiene stock.
  // Un producto con variantes no tiene fila de stock propia, así que vender el
  // producto "pelado" da sin stock aunque el total diga que hay.
  const vendibles = productos.flatMap((p) => {
    if (p.variantes?.length) {
      return p.variantes.filter(v => v.activo).map(v => ({
        id_producto: p.id,
        id_variante: v.id,
        nombre: p.nombre,
        detalle: v.label || v.sku || '',
        codigo: v.sku || p.codigo,
        stock: v.stock,
        precio: precioDe(v.precios) ?? precioDe(p.precios),
      }));
    }
    return [{
      id_producto: p.id,
      id_variante: null,
      nombre: p.nombre,
      detalle: '',
      codigo: p.codigo,
      stock: p.stock,
      precio: precioDe(p.precios),
    }];
  });

  const agregar = (prod) => {
    const yaEsta = lineas.find(l =>
      l.id_producto === prod.id_producto && l.id_variante === prod.id_variante);
    if (yaEsta) {
      setLineas(lineas.map(l => l === yaEsta ? { ...l, cantidad: l.cantidad + 1 } : l));
      return;
    }
    setLineas([...lineas, { ...prod, cantidad: 1 }]);
  };

  const quitar = (i) => setLineas(lineas.filter((_, j) => j !== i));
  const cambiarCantidad = (i, cantidad) =>
    setLineas(lineas.map((l, j) => j === i ? { ...l, cantidad: cantidad || 1 } : l));

  const subtotal = lineas.reduce((acc, l) => acc + (l.precio || 0) * l.cantidad, 0);

  useEffect(() => {
    if (lineas.length === 0) {
      setPromos({ promos: [], descuento: 0, envio_gratis: false });
      return;
    }
    let vigente = true;
    (async () => {
      const res = await api.promociones.simular(subtotal, lineas.map(l => ({
        id_producto: l.id_producto,
        cantidad: l.cantidad,
        total: (l.precio || 0) * l.cantidad,
      })));
      if (vigente && res.status === 'success') setPromos(res.data);
    })();
    return () => { vigente = false; };
  }, [lineas, subtotal]);

  const total = subtotal - (promos.descuento || 0);

  // El ajuste de la pasarela lo recalcula el server al cobrar; acá es solo para
  // que el vendedor vea el número antes de confirmar.
  const descuentoMetodo = metodos.find(m => m.id === metodo)?.descuento || 0;
  const ajustePago = Math.round(-total * descuentoMetodo) / 100;

  const confirmar = async () => {
    setConfirmando(true);
    try {
      const res = await api.admin.crearPedido({
        id_cliente: cliente,
        estado_pago: estadoPago,
        id_metodo_pago: estadoPago === 'pagado' ? metodo : null,
        avisar_por_mail: avisar,
        obs,
        items: lineas.map(l => ({
          id_producto: l.id_producto,
          id_variante: l.id_variante,
          cantidad: l.cantidad,
        })),
      });

      if (res.status === 'sin_stock') {
        message.warning(res.message || 'No hay stock suficiente');
        return;
      }
      if (res.status !== 'success') {
        message.error(res.message || 'No se pudo crear el pedido');
        return;
      }
      message.success(`Pedido #${res.data.id_pedido} creado`);
      setLineas([]);
      setObs('');
      setModal(false);
      navigate(clientPath('/admin/pedidos'));
    } finally {
      setConfirmando(false);
    }
  };

  const columnas = [
    { title: 'Producto', dataIndex: 'nombre', render: (v, r) => (
      <div>
        <div>{v}{r.detalle && <Text type="secondary"> · {r.detalle}</Text>}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>{r.codigo}</Text>
      </div>
    )},
    { title: 'Precio', dataIndex: 'precio', width: 130, align: 'right',
      render: (v) => v ? fmt(v) : <Tag color="warning">sin precio</Tag> },
    { title: 'Cantidad', dataIndex: 'cantidad', width: 110, align: 'center',
      render: (v, _, i) => (
        <InputNumber min={1} value={v} onChange={(n) => cambiarCantidad(i, n)} style={{ width: 80 }} />
      )},
    { title: 'Subtotal', width: 140, align: 'right',
      render: (_, r) => fmt((r.precio || 0) * r.cantidad) },
    { title: '', width: 50, align: 'center',
      render: (_, __, i) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => quitar(i)} />
      )},
  ];

  if (cargando) return <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>;

  const listo = cliente && lineas.length > 0;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Title level={4} style={{ marginBottom: 20 }}>Nuevo pedido</Title>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Buscador de productos */}
        <div style={{ flex: '1 1 460px', minWidth: 0 }}>
          <Input
            placeholder="Buscar producto por nombre o código"
            prefix={<SearchOutlined />}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onPressEnter={() => buscarProductos(busqueda)}
            style={{ marginBottom: 12 }}
            allowClear
          />
          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 8 }}>
            {vendibles.length === 0 ? (
              <Empty description="Sin productos" style={{ padding: 30 }} />
            ) : vendibles.map((p) => (
              <div key={`${p.id_producto}-${p.id_variante || 0}`} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderBottom: '1px solid #fafafa',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14 }}>
                    {p.nombre}
                    {p.detalle && <Text type="secondary"> · {p.detalle}</Text>}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {p.codigo} · stock {p.stock} · {p.precio ? fmt(p.precio) : 'sin precio'}
                  </Text>
                </div>
                <Button
                  size="small" icon={<PlusOutlined />}
                  disabled={p.stock <= 0 || !p.precio}
                  onClick={() => agregar(p)}
                >
                  Agregar
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* El pedido que se está armando */}
        <div style={{ flex: '1 1 460px', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Select
              showSearch
              placeholder="Elegí el cliente"
              style={{ flex: 1, minWidth: 0 }}
              value={cliente}
              onChange={setCliente}
              optionFilterProp="label"
              options={clientes.map(c => ({
                value: c.id,
                label: [c.nombre, c.dni && `DNI ${c.dni}`, c.cuit && `CUIT ${c.cuit}`].filter(Boolean).join(' — '),
              }))}
            />
            <Button icon={<UserAddOutlined />} onClick={() => setAltaCliente(true)}>Nuevo cliente</Button>
          </div>

          <Table
            size="small"
            rowKey={(_, i) => i}
            columns={columnas}
            dataSource={lineas}
            pagination={false}
            locale={{ emptyText: 'Agregá productos desde la izquierda' }}
          />

          {lineas.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text type="secondary">Subtotal</Text>
                <Text type="secondary">{fmt(subtotal)}</Text>
              </div>
              {promos.promos?.map((p) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ color: primary, fontSize: 13 }}>{p.nombre}</Text>
                  <Text style={{ color: primary, fontSize: 13 }}>- {fmt(p.descuento)}</Text>
                </div>
              ))}
              {promos.envio_gratis && (
                <div style={{ marginBottom: 6 }}><Tag color="success">Envío gratis</Tag></div>
              )}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0',
              }}>
                <Text strong style={{ fontSize: 16 }}>Total</Text>
                <Text strong style={{ fontSize: 20 }}>{fmt(total)}</Text>
              </div>
            </div>
          )}

          <Button
            type="primary" block size="large" disabled={!listo}
            onClick={() => setModal(true)}
            style={{ marginTop: 16, background: listo ? primary : undefined, borderColor: listo ? primary : undefined }}
          >
            Confirmar pedido
          </Button>
        </div>
      </div>

      <ClienteFormModal
        open={altaCliente}
        onClose={() => setAltaCliente(false)}
        onCreado={(nuevo) => {
          setAltaCliente(false);
          setClientes((cs) => [nuevo, ...cs.filter(c => c.id !== nuevo.id)]);
          setCliente(nuevo.id);
        }}
        accionExistente={{
          label: 'Usar este cliente',
          onClick: (c) => {
            setClientes((cs) => (cs.some(x => x.id === c.id) ? cs : [c, ...cs]));
            setCliente(c.id);
          },
        }}
      />

      <Modal
        open={modal}
        title="Confirmar pedido"
        onCancel={() => setModal(false)}
        onOk={confirmar}
        okText="Crear pedido"
        confirmLoading={confirmando}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>¿Cómo está el pago?</Text>
          <Radio.Group value={estadoPago} onChange={(e) => setEstadoPago(e.target.value)}>
            <Radio value="pendiente">Pendiente de pago</Radio>
            <Radio value="pagado">Ya pagado</Radio>
          </Radio.Group>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            {estadoPago === 'pagado'
              ? 'El stock se descuenta ahora.'
              : 'El stock queda reservado hasta que se apruebe el cobro.'}
          </Text>
          {estadoPago === 'pagado' && (
            <Select
              placeholder="¿Con qué se cobró?"
              style={{ width: '100%', marginTop: 12 }}
              value={metodo}
              onChange={setMetodo}
              options={metodos.map(m => ({
                value: m.id,
                label: m.descuento
                  ? `${m.nombre} (${m.descuento > 0 ? '-' : '+'}${Math.abs(m.descuento)}%)`
                  : m.nombre,
              }))}
            />
          )}
          {estadoPago === 'pagado' && ajustePago !== 0 && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Con este medio de pago el total queda en <b>{fmt(total + ajustePago)}</b>.
            </Text>
          )}
          {estadoPago === 'pagado' ? (
            <>
              <Checkbox
                checked={avisar}
                onChange={(e) => setAvisar(e.target.checked)}
                style={{ marginTop: 12 }}
              >
                Avisarle al cliente por mail
              </Checkbox>
              <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                Dejalo sin tildar si el cliente está presente.
              </Text>
            </>
          ) : (
            <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
              El cliente recibe el mail cuando apruebes el cobro desde Cobros.
            </Text>
          )}
        </div>
        <Input.TextArea
          placeholder="Observaciones (opcional)"
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          rows={2}
        />
      </Modal>
    </div>
  );
};
